import * as React from 'react';
import {
  Typography, Box, Button, Grid, Dialog, DialogTitle, DialogContent, DialogActions,
  Paper, Tabs, Tab, Chip, CircularProgress, Switch, FormControlLabel, IconButton,
  useMediaQuery, useTheme, FormControl, InputLabel, Select, MenuItem, Collapse, TextField,
} from '@mui/material';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Table, Tag, Empty, notification } from 'antd';
import { ArrowBack, Refresh, Delete, Search, FilterList, FilterListOff, Print } from '@mui/icons-material';
import moment from 'moment';
import axios from 'axios';
import {
  getBazaar, acceptBazaar, fulfillBazaarOutbound, completeBazaar, rejectBazaar, downloadBazaarCountSheet,
  createBazaarCashier, updateBazaarCashier, deleteBazaarCashier, getBazaarPurchases,
} from '../../app/store/actions/imsAdminActions';
import { MainInput, MainPassword, main_button, sec_button } from '../../app/components/Styles';
import ActionDialog from '../../app/components/ActionDialog';
import {
  BAZAAR_STATUS_COLORS, bazaarStatusLabel, bazaarModeLabel,
} from '../../app/components/bazaarUtils';

const REFRESH_PASSWORD_USER_IDS = ['65cba5b59d76a5203fb0cdae', '68fea2723820930efc7db2b5', '685ea490b34528b6f3841909'];

export const BazaarAdminDetail = ({
  getBazaar, acceptBazaar, fulfillBazaarOutbound, completeBazaar, rejectBazaar, downloadBazaarCountSheet,
  createBazaarCashier, updateBazaarCashier, deleteBazaarCashier, getBazaarPurchases,
  bazaarData, bazaarPurchases, isLoading, isWaiting, user, token,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tab, setTab] = React.useState(0);
  const [cashierOpen, setCashierOpen] = React.useState(false);
  const [completeOpen, setCompleteOpen] = React.useState(false);
  const [rejectOpen, setRejectOpen] = React.useState(false);
  const [rejectReason, setRejectReason] = React.useState('');
  const [cashierForm, setCashierForm] = React.useState({ username: '', password: '', name: '' });
  const [purchaseModal, setPurchaseModal] = React.useState(null);
  const [showPurchaseFilters, setShowPurchaseFilters] = React.useState(false);
  const [purchaseSearch, setPurchaseSearch] = React.useState('');
  const [purchasePayment, setPurchasePayment] = React.useState('');
  const [purchasePage, setPurchasePage] = React.useState(1);
  const [purchasePageSize, setPurchasePageSize] = React.useState(10);
  const [refreshDialogOpen, setRefreshDialogOpen] = React.useState(false);
  const [refreshPassword, setRefreshPassword] = React.useState('');
  const [refreshError, setRefreshError] = React.useState(null);
  const [refreshValidated, setRefreshValidated] = React.useState(false);
  const [refreshLoading, setRefreshLoading] = React.useState(false);

  const PAYMENT_METHODS = ['Cash', 'Visa', 'Instapay'];
  const requiresRefreshPassword = React.useMemo(
    () => REFRESH_PASSWORD_USER_IDS.includes(user?._id),
    [user]
  );

  const bazaar = bazaarData?.bazaar;
  const status = bazaar?.displayStatus || bazaar?.status;
  const stats = bazaarData?.stats || {};

  const load = () => getBazaar(id);
  React.useEffect(() => { load(); }, [id]);

  React.useEffect(() => {
    if (tab === 4 && bazaar?.fulfillmentMode === 'POS') {
      getBazaarPurchases(id, {
        page: purchasePage,
        limit: purchasePageSize,
        search: purchaseSearch || undefined,
        paymentMethod: purchasePayment || undefined,
      });
    }
  }, [tab, id, purchasePage, purchasePageSize, purchasePayment, bazaar?.fulfillmentMode]);

  const handlePurchaseSearch = () => {
    setPurchasePage(1);
    getBazaarPurchases(id, {
      page: 1,
      limit: purchasePageSize,
      search: purchaseSearch || undefined,
      paymentMethod: purchasePayment || undefined,
    });
  };

  const handleOutbound = async () => {
    try {
      const { prefilled } = await fulfillBazaarOutbound(id);
      navigate(`/ims/admin/outbounds/create?bazaarId=${id}`, { state: { prefilled } });
    } catch (err) {
      // notification from action
    }
  };

  const submitReject = () => {
    const reason = rejectReason.trim();
    if (!reason) {
      notification.error({ message: 'Rejection reason is required' });
      return;
    }
    rejectBazaar(id, reason).then(() => {
      setRejectOpen(false);
      setRejectReason('');
    }).catch(() => {});
  };

  const submitCashier = () => {
    createBazaarCashier(id, cashierForm).then(() => {
      setCashierOpen(false);
      setCashierForm({ username: '', password: '', name: '' });
      load();
    });
  };

  const toggleCashierActive = (cashier) => {
    updateBazaarCashier(id, cashier._id, { isActive: !cashier.isActive }).then(load);
  };

  const removeCashier = (cashierId) => {
    deleteBazaarCashier(id, cashierId).then(load);
  };

  const handleCompleteClick = () => {
    if (requiresRefreshPassword && !refreshValidated) {
      setRefreshDialogOpen(true);
      return;
    }
    setCompleteOpen(true);
  };

  const handleRefreshClose = () => {
    if (refreshLoading) {
      return;
    }
    setRefreshDialogOpen(false);
    setRefreshPassword('');
    setRefreshError(null);
  };

  const handleRefreshConfirm = async () => {
    if (!refreshPassword) {
      setRefreshError('Outbound password is required.');
      return;
    }
    setRefreshLoading(true);
    setRefreshError(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE}/IMAdmin/outbound/refreshPass`,
        { password: refreshPassword },
        {
          headers: {
            'x-access-token': token,
          },
        }
      );

      if (response.status === 200) {
        setRefreshValidated(true);
        setRefreshDialogOpen(false);
        setRefreshPassword('');
        completeBazaar(id, navigate);
      } else {
        setRefreshError('Invalid password. Please try again.');
      }
    } catch (error) {
      setRefreshError(error?.response?.data?.message || 'Unable to verify password. Please try again.');
    } finally {
      setRefreshLoading(false);
    }
  };

  if (isLoading && !bazaar) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress sx={{ color: 'var(--secColor)' }} />
      </Box>
    );
  }

  const overviewTab = (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Typography><strong>Brand:</strong> {bazaar?.vendor?.brandName}</Typography>
        <Typography><strong>Location:</strong> {bazaar?.location}</Typography>
        <Typography><strong>Dates:</strong> {moment(bazaar?.startDate).format('LL')} – {moment(bazaar?.endDate).format('LL')}</Typography>
        <Typography><strong>Times:</strong> {bazaar?.startTime || '—'} – {bazaar?.endTime || '—'}</Typography>
        <Typography><strong>Mode:</strong> {bazaarModeLabel(bazaar?.fulfillmentMode)}</Typography>
        {bazaar?.warehouse?.name && (
          <Typography><strong>Warehouse:</strong> {bazaar.warehouse.name}</Typography>
        )}
        {bazaar?.status === 'Rejected' && bazaar?.rejectionReason && (
          <Typography sx={{ mt: 1 }}><strong>Rejection reason:</strong> {bazaar.rejectionReason}</Typography>
        )}
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">POS stats</Typography>
          <Typography><strong>Revenue:</strong> {(stats.totalRevenue || 0).toLocaleString()} EGP</Typography>
          <Typography><strong>Purchases:</strong> {stats.purchaseCount || 0}</Typography>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6" sx={{ mb: 1 }}>Requested items</Typography>
        <Table size="small" pagination={false}
          locale={{ emptyText: <Empty description="No items" /> }}
          dataSource={bazaar?.lineItems?.map((li, i) => ({
            key: i,
            sku: li.product?.sku || li.product?.externalId,
            name: li.product?.name,
            qty: li.quantityRequested,
            available: bazaarData?.mainWarehouseStock?.[String(li.product?._id || li.product)] ?? 0,
          }))}
          columns={[
            { title: 'SKU', dataIndex: 'sku' },
            { title: 'Name', dataIndex: 'name' },
            { title: 'Requested', dataIndex: 'qty' },
            {
              title: 'Main WH available',
              dataIndex: 'available',
              render: (val) => <Chip size="small" color={val > 0 ? 'success' : 'error'} label={val} />,
            },
          ]}
        />
      </Grid>
      {bazaar?.fulfilledItems?.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 1 }}>Sent vs requested</Typography>
          <Table size="small" pagination={false}
            dataSource={bazaar.fulfilledItems.map((fi, i) => ({
              key: i,
              sku: fi.product?.sku || fi.product?.externalId,
              name: fi.product?.name,
              requested: fi.quantityRequested,
              sent: fi.quantitySent,
              variance: fi.quantitySent - fi.quantityRequested,
            }))}
            columns={[
              { title: 'SKU', dataIndex: 'sku' },
              { title: 'Name', dataIndex: 'name' },
              { title: 'Requested', dataIndex: 'requested' },
              { title: 'Sent', dataIndex: 'sent' },
              {
                title: 'Variance',
                render: (_, row) => (
                  <Tag color={row.variance === 0 ? 'green' : 'orange'}>
                    {row.variance > 0 ? `+${row.variance}` : row.variance}
                  </Tag>
                ),
              },
            ]}
          />
        </Grid>
      )}
    </Grid>
  );

  const shipmentsTab = (
    (bazaarData?.shipments || []).length ? (
      bazaarData.shipments.map((sh, idx) => (
        <Paper key={sh._id || idx} sx={{ p: 2, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">{moment(sh.createdAt).format('LLL')}</Typography>
          <Table size="small" pagination={false}
            dataSource={sh.items?.map((item, i) => ({
              key: i,
              sku: item.product?.sku || item.product?.externalId,
              name: item.product?.name,
              sent: item.quantitySent,
            }))}
            columns={[
              { title: 'SKU', dataIndex: 'sku' },
              { title: 'Name', dataIndex: 'name' },
              { title: 'Qty sent', dataIndex: 'sent' },
            ]}
          />
        </Paper>
      ))
    ) : <Empty description="No shipments yet" />
  );

  const stockTab = (
    <Table size="small"
      locale={{ emptyText: <Empty description="No stock at bazaar warehouse" /> }}
      dataSource={(bazaarData?.stock || []).map((s, i) => ({
        key: s._id || i,
        sku: s.product?.sku || s.product?.externalId,
        name: s.product?.name,
        amount: s.amount,
      }))}
      columns={[
        { title: 'SKU', dataIndex: 'sku' },
        { title: 'Name', dataIndex: 'name' },
        { title: 'On hand', dataIndex: 'amount' },
      ]}
    />
  );

  const cashiersTab = (
    <>
      <Button sx={{ ...main_button, mb: 2 }} onClick={() => setCashierOpen(true)} disabled={bazaar?.fulfillmentMode !== 'POS'}>
        Add cashier
      </Button>
      <Table size="small" pagination={false}
        locale={{ emptyText: <Empty description="No cashiers yet" /> }}
        dataSource={(bazaarData?.cashiers || []).map((c) => ({ ...c, key: c._id }))}
        columns={[
          { title: 'Username', dataIndex: 'username' },
          { title: 'Name', dataIndex: 'name' },
          {
            title: 'Active',
            render: (_, r) => (
              <FormControlLabel
                control={<Switch checked={r.isActive !== false} onChange={() => toggleCashierActive(r)} size="small" />}
                label={r.isActive !== false ? 'Yes' : 'No'}
              />
            ),
          },
          {
            title: '',
            render: (_, r) => (
              <IconButton color="error" onClick={() => removeCashier(r._id)} size="small">
                <Delete />
              </IconButton>
            ),
          },
        ]}
      />
    </>
  );

  const purchasesTab = (
    <>
      <Button sx={{ ...main_button, mb: 2 }} onClick={() => setShowPurchaseFilters(!showPurchaseFilters)}>
        {showPurchaseFilters ? <FilterListOff /> : <FilterList />} Filter
      </Button>
      <Collapse in={showPurchaseFilters}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <MainInput
              fullWidth
              label="Search by receipt #"
              placeholder="#POS-BRAND-1001"
              value={purchaseSearch}
              onChange={(e) => setPurchaseSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handlePurchaseSearch();
                }
              }}
              InputProps={{ endAdornment: <Search sx={{ cursor: 'pointer' }} onClick={handlePurchaseSearch} /> }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Payment</InputLabel>
              <Select value={purchasePayment} label="Payment"
                onChange={(e) => { setPurchasePayment(e.target.value); setPurchasePage(1); }}>
                <MenuItem value="">All</MenuItem>
                {PAYMENT_METHODS.map((m) => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button sx={sec_button} fullWidth onClick={handlePurchaseSearch}>Search</Button>
          </Grid>
        </Grid>
      </Collapse>
      <Table
        loading={isLoading}
        locale={{ emptyText: <Empty description="No purchases yet" /> }}
        dataSource={(bazaarPurchases?.docs || []).map((p) => ({ ...p, key: p._id }))}
        columns={[
          { title: 'Receipt #', dataIndex: 'purchaseNumber' },
          { title: 'Status', dataIndex: 'status', render: (val) => val || 'Active' },
          { title: 'Payment', dataIndex: 'paymentMethod' },
          { title: 'Customer', dataIndex: 'customerName', render: (val) => val || '—' },
          { title: 'Cashier', render: (_, r) => r.cashier?.name || '—' },
          { title: 'Total', render: (_, r) => `${parseFloat(r.total || 0).toLocaleString()} EGP` },
          { title: 'Date', render: (_, r) => moment(r.createdAt).format('LLL') },
          {
            title: '',
            render: (_, r) => (
              <Button size="small" onClick={() => setPurchaseModal(r)}>View</Button>
            ),
          },
        ]}
        onChange={(pagination) => {
          setPurchasePage(pagination.current);
          setPurchasePageSize(pagination.pageSize);
        }}
        pagination={{
          current: purchasePage,
          pageSize: purchasePageSize,
          total: bazaarPurchases?.totalDocs || 0,
          showSizeChanger: true,
        }}
      />
    </>
  );

  return (
    <Box sx={{ width: '100%', padding: isMobile ? 2 : 4, mt: 1 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/ims/admin/bazaars')} sx={{ mb: 2, ...sec_button }}>
        All bazaars
      </Button>

      <Grid container justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <Box>
          <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ color: 'var(--secColor)' }}>{bazaar?.name}</Typography>
          <Tag color={BAZAAR_STATUS_COLORS[status]}>{bazaarStatusLabel(status)}</Tag>
        </Box>
        <Button sx={sec_button} startIcon={<Refresh />} onClick={load} disabled={isLoading}>Refresh</Button>
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={1}>
          {bazaar?.fulfillmentMode === 'POS' && bazaar?.status === 'Pending' && (
            <Grid item>
              <Button sx={main_button} onClick={() => acceptBazaar(id, navigate)} disabled={isWaiting}>
                Accept POS bazaar
              </Button>
            </Grid>
          )}
          {bazaar?.fulfillmentMode === 'Outbound' && bazaar?.status === 'Pending' && (
            <Grid item><Button sx={main_button} onClick={handleOutbound}>Create outbound</Button></Grid>
          )}
          {bazaar?.status === 'Pending' && (
            <Grid item>
              <Button sx={sec_button} color="error" onClick={() => setRejectOpen(true)} disabled={isWaiting}>
                Reject
              </Button>
            </Grid>
          )}
          {bazaar?.lineItems?.length > 0 && (
            <Grid item>
              <Button sx={sec_button} startIcon={<Print />} onClick={() => downloadBazaarCountSheet(id)} disabled={isWaiting}>
                Print count sheet
              </Button>
            </Grid>
          )}
          {bazaar?.fulfillmentMode === 'POS' && ['Accepted', 'Fulfilled'].includes(bazaar?.status) && status !== 'Completed' && (
            <>
              <Grid item>
                <Button sx={main_button} onClick={() => navigate(`/ims/admin/bazaars/${id}/send-stock`)}>
                  Send stock
                </Button>
              </Grid>
              <Grid item>
                <Button sx={sec_button} onClick={() => setCashierOpen(true)}>Add cashier</Button>
              </Grid>
            </>
          )}
          {bazaar?.fulfillmentMode === 'POS' && status !== 'Completed' && bazaar?.status !== 'Pending' && bazaar?.status !== 'Cancelled' && bazaar?.status !== 'Rejected' && (
            <Grid item>
              <Button sx={sec_button} color="warning" onClick={handleCompleteClick} disabled={isWaiting}>
                Complete bazaar
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Paper sx={{ p: isMobile ? 1 : 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant={isMobile ? 'scrollable' : 'standard'} scrollButtons="auto">
          <Tab label="Overview" />
          <Tab label="Shipments" />
          <Tab label="Bazaar stock" />
          <Tab label="Cashiers" />
          {bazaar?.fulfillmentMode === 'POS' && <Tab label="Purchases" />}
        </Tabs>
        <Box sx={{ mt: 2 }}>
          {tab === 0 && overviewTab}
          {tab === 1 && shipmentsTab}
          {tab === 2 && stockTab}
          {tab === 3 && cashiersTab}
          {tab === 4 && bazaar?.fulfillmentMode === 'POS' && purchasesTab}
        </Box>
      </Paper>

      <Dialog open={Boolean(purchaseModal)} onClose={() => setPurchaseModal(null)} maxWidth="md" fullWidth>
        <DialogTitle>Purchase — {purchaseModal?.purchaseNumber}</DialogTitle>
        <DialogContent dividers>
          {purchaseModal && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography><strong>Date:</strong> {moment(purchaseModal.createdAt).format('LLL')}</Typography>
                <Typography><strong>Payment:</strong> {purchaseModal.paymentMethod}</Typography>
                {(purchaseModal.refundTotal || 0) > 0 && (
                  <>
                    <Typography><strong>Original total:</strong> {(purchaseModal.originalTotal ?? purchaseModal.lineItems?.reduce((a, li) => a + li.lineTotal, 0))?.toLocaleString()} EGP</Typography>
                    <Typography><strong>Refunded:</strong> −{purchaseModal.refundTotal?.toLocaleString()} EGP</Typography>
                  </>
                )}
                <Typography><strong>Current total:</strong> {purchaseModal.total?.toLocaleString()} EGP</Typography>
                <Typography><strong>Status:</strong> {purchaseModal.status || 'Active'}</Typography>
                {purchaseModal.customerName && (
                  <Typography><strong>Customer:</strong> {purchaseModal.customerName}</Typography>
                )}
                {purchaseModal.buyerEmail && (
                  <Typography><strong>Email:</strong> {purchaseModal.buyerEmail}</Typography>
                )}
                {purchaseModal.cashier?.name && (
                  <Typography><strong>Cashier:</strong> {purchaseModal.cashier.name}</Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <Table
                  size="small"
                  pagination={false}
                  dataSource={purchaseModal.lineItems?.map((li, i) => ({
                    key: i,
                    sku: li.product?.sku || li.product?.externalId,
                    name: li.product?.name,
                    bought: li.quantity,
                    returned: li.quantityReturned || 0,
                    kept: li.quantity - (li.quantityReturned || 0),
                    unitPrice: li.unitPrice,
                    lineTotal: (li.quantity - (li.quantityReturned || 0)) * li.unitPrice,
                  }))}
                  columns={[
                    { title: 'SKU', dataIndex: 'sku' },
                    { title: 'Name', dataIndex: 'name' },
                    { title: 'Bought', dataIndex: 'bought' },
                    { title: 'Returned', dataIndex: 'returned' },
                    { title: 'Kept', dataIndex: 'kept' },
                    { title: 'Unit', render: (_, r) => `${r.unitPrice?.toLocaleString()} EGP` },
                    { title: 'Total', render: (_, r) => `${r.lineTotal?.toLocaleString()} EGP` },
                  ]}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurchaseModal(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cashierOpen} onClose={() => setCashierOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create cashier</DialogTitle>
        <DialogContent>
          <MainInput fullWidth label="Display name" value={cashierForm.name}
            onChange={(e) => setCashierForm({ ...cashierForm, name: e.target.value })} sx={{ mt: 1 }} />
          <MainInput fullWidth label="Username" value={cashierForm.username}
            onChange={(e) => setCashierForm({ ...cashierForm, username: e.target.value })} sx={{ mt: 2 }} />
          <MainPassword fullWidth label="Password" value={cashierForm.password}
            onChange={(e) => setCashierForm({ ...cashierForm, password: e.target.value })} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCashierOpen(false)}>Cancel</Button>
          <Button sx={main_button} onClick={submitCashier} disabled={isWaiting}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject bazaar?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The partner will be emailed this reason. This cannot be undone.
          </Typography>
          <MainInput
            fullWidth
            required
            multiline
            minRows={3}
            label="Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button sx={sec_button} onClick={submitReject} disabled={isWaiting || !rejectReason.trim()}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      <ActionDialog
        open={completeOpen}
        handleClose={() => setCompleteOpen(false)}
        handleAccept={() => { setCompleteOpen(false); completeBazaar(id, navigate); }}
        title="Complete bazaar?"
        content="This closes the bazaar and creates a return inbound for remaining stock at the bazaar warehouse. This cannot be undone."
      />

      <Dialog open={refreshDialogOpen} onClose={handleRefreshClose}>
        <DialogTitle>Outbound Password Required</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            This closes the bazaar and creates a return inbound for remaining stock at the bazaar warehouse. This cannot be undone.
          </Typography>
          <Typography sx={{ mb: 2 }}>
            Please enter your outbound password to complete this bazaar.
          </Typography>
          <TextField
            margin="normal"
            label="Outbound Password"
            type="password"
            fullWidth
            value={refreshPassword}
            onChange={({ target }) => setRefreshPassword(target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleRefreshConfirm();
              }
            }}
            autoFocus
            error={Boolean(refreshError)}
            helperText={refreshError}
            disabled={refreshLoading || isWaiting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRefreshClose} disabled={refreshLoading}>
            Cancel
          </Button>
          <Button onClick={handleRefreshConfirm} disabled={refreshLoading || isWaiting}>
            {refreshLoading ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  bazaarData: state.records,
  bazaarPurchases: state.records.bazaarPurchases,
  isLoading: state.records.isLoading,
  isWaiting: state.wait.isLoading,
  user: state?.auth?.user,
  token: state?.auth?.token,
});

export default connect(mapStateToProps, {
  getBazaar, acceptBazaar, fulfillBazaarOutbound, completeBazaar, rejectBazaar, downloadBazaarCountSheet,
  createBazaarCashier, updateBazaarCashier, deleteBazaarCashier, getBazaarPurchases,
})(BazaarAdminDetail);
